import { useMemo } from 'react';
import { ResourceAvatar } from '@ui';
import { GroundTripPassenger, PassengerType } from '@/api/apis/drive-service';
import { Uuid } from '@/api/types';
import { getFullName } from '@/utils/helpers';
import { CaseResourceType } from '@/api/apis/case';
import { organSuffixes } from '@/constants/case/case.constants';
import { queryClient } from '@/queries';
import { hubQueries } from '@/queries/hub';

const getHubCodeAbbr = (hubId?: string | null) => {
  if (hubId) {
    const hub = queryClient.getQueryData(hubQueries.item(hubId).queryKey);

    if (!hub) return '';

    return `(${hub.code})`;
  }

  return '';
};

type TripPassengersProps = {
  type?: 'full' | 'compact';
  ids: Uuid[];
  tripPassengers: GroundTripPassenger[];
};

type TripPassengerItemType = {
  data: GroundTripPassenger;
} & Pick<TripPassengersProps, 'type'>;

const TripPassengerItem = ({ type, data }: TripPassengerItemType) => {
  const isFull = type === 'full';
  const avatarRadiusType = isFull ? 'md' : 'full';

  const avatarType = useMemo(() => {
    switch (data.passengerType) {
      case PassengerType.Employee:
        switch (data.resourceType) {
          case CaseResourceType.Surgeon:
            return 'retrieval-surgeon';
          case CaseResourceType.ClinicalSpecialist:
            return 'clinical-specialist';
          default:
            return 'user';
        }
      case PassengerType.ExternalPassenger:
        return 'user';
      case PassengerType.Scs:
        return 'scs';
      default:
        return 'device';
    }
  }, [data.resourceType, data.passengerType]);

  const roleAbbr = useMemo(() => {
    switch (data.passengerType) {
      case PassengerType.Employee:
        switch (data.resourceType) {
          case CaseResourceType.Surgeon:
            return '(S)';
          case CaseResourceType.ClinicalSpecialist:
            return '(CS)';
          default:
            return '';
        }
      case PassengerType.OcsConsole:
        return getHubCodeAbbr(data.hubId);
      case PassengerType.ExternalPassenger:
        return '(External)';
      default:
        return '';
    }
  }, [data]);

  const isOcsDevice = data.passengerType === PassengerType.OcsConsole;
  const isScsDevice = data.passengerType === PassengerType.Scs;
  const isDevice = isOcsDevice || isScsDevice;
  const showFullName = isFull || !isDevice;
  const abbreviation = roleAbbr ? ` ${roleAbbr}` : '';

  switch (data.passengerType) {
    case PassengerType.Employee:
    case PassengerType.ExternalPassenger:
      const avatarUrl = data.passengerType === PassengerType.Employee ? data.profilePhoto : undefined;

      return (
        <div className="flex gap-1 items-center">
          <ResourceAvatar
            size="sm"
            radius={avatarRadiusType}
            name={getFullName(data.firstName, data.lastName)}
            type={avatarType}
            src={avatarUrl || ''}
          />
          <span className="text-s-r text-base-10">{`${getFullName(data.firstName, data.lastName)}${abbreviation}`}</span>
        </div>
      );
    case PassengerType.OcsConsole:
      const deviceSerialNumber =
        data.organType && data.fullSerialNumber ? `${organSuffixes[data.organType]}-${data.fullSerialNumber}` : '-';
      return (
        <div className="flex gap-1 items-center">
          <ResourceAvatar
            size="sm"
            organ={data.organType}
            radius={avatarRadiusType}
            name={deviceSerialNumber}
            type={avatarType}
          />
          {showFullName && <span className="text-s-r text-base-10">{`${deviceSerialNumber}${abbreviation}`}</span>}
        </div>
      );
    case PassengerType.Scs:
      return (
        <div className="flex gap-1 items-center">
          <ResourceAvatar size="sm" radius={avatarRadiusType} type={avatarType} />
        </div>
      );
    default:
      return null;
  }
};

export const TripPassengers = ({ type = 'full', ids, tripPassengers }: TripPassengersProps) => {
  const passengers = useMemo(() => {
    return ids
      .map((id) => {
        return tripPassengers.find((p) => p.passengerId === id);
      })
      .filter((p): p is GroundTripPassenger => Boolean(p));
  }, [ids, tripPassengers]);

  if (!passengers.length) return null;

  return (
    <div className="flex gap-1 items-center flex-wrap">
      {passengers.map((passenger) => (
        <TripPassengerItem key={passenger.passengerId} data={passenger} type={type} />
      ))}
    </div>
  );
};
